
import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  MenuItem,
  Box,
  FormHelperText
} from '@mui/material';
import FloatingWindow from './FloatingWindow';
import { useAuth } from '../context/AuthContext';

const CreateIdeaWindow = ({ open, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    projectName: '',
    acronymName: '',
    keywords: '',
    objectives: '',
    description: '',
    innovationLevel: '',
    budget: '',
    adviceId: null,
    selectedEmails: []
  });
  

  const [advices, setAdvices] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const { user } = useAuth();
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch users
        const usersResponse = await fetch('https://api.hub4food.colab4food.com/api/users');
        if (!usersResponse.ok) throw new Error('Failed to fetch users');
        const usersData = await usersResponse.json();
        setUsers(usersData.map(user => ({
          id: user._id || user.id,
          name: user.name,
          email: user.email,
        })));

        // Fetch advices
        const advicesResponse = await fetch('https://api.hub4food.colab4food.com/api/advices');
        if (advicesResponse.ok) {
          setAdvices(await advicesResponse.json());
        } else {
          console.error("Failed to fetch advices");
        }

        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    if (open) {
      fetchData();
    }
  }, [open]);

  const handleChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    // Clear error when user types
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.projectName) errors.projectName = 'Project name is required';
    if (!formData.acronymName) errors.acronymName = 'Acronym name is required';
    if (!formData.keywords) errors.keywords = 'Keywords are required';
    if (!formData.objectives) errors.objectives = 'Objectives are required';
    if (!formData.description) errors.description = 'Description is required';
    if (!formData.innovationLevel) errors.innovationLevel = 'Innovation level is required';
    if (!formData.budget) errors.budget = 'Budget is required';
   

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const adviceIdValue = formData.adviceId === '' ? null : formData.adviceId;

    const ideaData = {
      project_name: formData.projectName,
      acronym_name: formData.acronymName,
      keywords: formData.keywords,
      objectives: formData.objectives,
      description: formData.description,
      innovation_level: formData.innovationLevel,
      budget: parseFloat(formData.budget) || 0,
      advice_id: adviceIdValue,
      co_authors: formData.selectedEmails.map(user => user.id),
      user_id: user.userId
    };
    
    onCreate(ideaData);
    onClose();
  };

  return (
    <FloatingWindow
      title="Create New Idea"
      open={open}
      onClose={onClose}
      initialSize={{ width: 800, height: 700 }}
    >
      <Grid container spacing={2} sx={{ p: 2 }}>
        {/* Project Name */}
        <Grid item xs={12}>
          <TextField
            label="Project Name"
            fullWidth
            required
            value={formData.projectName}
            onChange={handleChange('projectName')}
            error={!!formErrors.projectName}
            helperText={formErrors.projectName}
          />
        </Grid>

        {/* Acronym Name */}
        <Grid item xs={12}>
          <TextField
            label="Acronym Name"
            fullWidth
            required
            value={formData.acronymName}
            onChange={handleChange('acronymName')}
            error={!!formErrors.acronymName}
            helperText={formErrors.acronymName}
          />
        </Grid>

        {/* Keywords */}
        <Grid item xs={12}>
          <TextField
            label="Keywords"
            fullWidth
            required
            value={formData.keywords}
            onChange={handleChange('keywords')}
            error={!!formErrors.keywords}
            helperText={formErrors.keywords}
          />
        </Grid>

        {/* Budget */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Budget (€)"
            fullWidth
            required
            type="number"
            value={formData.budget}
            onChange={handleChange('budget')}
            error={!!formErrors.budget}
            helperText={formErrors.budget}
            InputProps={{
              inputProps: { min: 0 }
            }}
          />
        </Grid>

        {/* Calls */}
        <Grid item xs={12} md={6}>
  <FormControl fullWidth error={!!formErrors.adviceId}>
    <InputLabel>Call</InputLabel>
    <Select
      value={formData.adviceId}
      onChange={handleChange('adviceId')}
      label="Call *"
      MenuProps={{
        style: { zIndex: 1400 }, 
        disablePortal: true, 
        container: document.getElementById('root') 
      }}
    >
              {loading ? (
                <MenuItem disabled>Loading calls...</MenuItem>
              ) : error ? (
                <MenuItem disabled>Error loading calls</MenuItem>
              ) : (
                advices.map((advice) => (
                  <MenuItem key={advice.id} value={advice.id}>
                    {advice.title}
                  </MenuItem>
                ))
              )}
            </Select>
            {formErrors.adviceId && (
              <FormHelperText>{formErrors.adviceId}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Partners */}
        <Grid item xs={12}>
  <FormControl fullWidth>
    <InputLabel>Partners</InputLabel>
    <Select
      multiple
      value={formData.selectedEmails}
      onChange={(e) => setFormData({...formData, selectedEmails: e.target.value})}
      label="Partners"
      renderValue={(selected) => selected.map(user => user.email).join(', ')}
      MenuProps={{
        style: { zIndex: 1400 }, 
        disablePortal: true, 
        container: document.getElementById('root') 
      }}
            >
              {users.map(user => (
                <MenuItem key={user.id} value={user}>
                  <Checkbox checked={formData.selectedEmails.some(selected => selected.id === user.id)} />
                  <ListItemText primary={`${user.name} (${user.email})`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* SMART Project Objectives */}
        <Grid item xs={12}>
          <TextField
            label="SMART Project Objectives"
            fullWidth
            required
            multiline
            rows={4}
            value={formData.objectives}
            onChange={handleChange('objectives')}
            error={!!formErrors.objectives}
            helperText={formErrors.objectives}
          />
        </Grid>

        {/* Description */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Description"
            fullWidth
            required
            multiline
            rows={6}
            value={formData.description}
            onChange={(e) => {
              if (e.target.value.length <= 200) {
                setFormData({...formData, description: e.target.value});
              }
              if (formErrors.description) {
                setFormErrors({...formErrors, description: null});
              }
            }}
            inputProps={{
              maxLength: 200,
            }}
            error={!!formErrors.description}
            helperText={
              formErrors.description || 
              `${formData.description.length}/200 characters`
            }
          />
        </Grid>

        {/* Innovative Level */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Innovative Level"
            fullWidth
            required
            multiline
            rows={6}
            value={formData.innovationLevel}
            onChange={handleChange('innovationLevel')}
            error={!!formErrors.innovationLevel}
            helperText={formErrors.innovationLevel}
          />
        </Grid>
      </Grid>

      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid #eee',
        display: 'flex', 
        justifyContent: 'flex-end' 
      }}>
        <Button 
          onClick={onClose} 
          sx={{ mr: 2 }}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
        >
          Save Idea
        </Button>
      </Box>
    </FloatingWindow>
  );
};

export default CreateIdeaWindow;